import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Zap, 
  Briefcase, 
  Wrench, 
  RefreshCw, 
  Building2, 
  Leaf, 
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Star,
  TrendingUp,
  Users,
  Award,
  Target,
  Send,
  Clock3,
  ShieldCheck,
  ExternalLink,
  Loader2,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  companyInfo, 
  statistics, 
  divisions, 
  projects, 
  caseStudies,
  operativitaCantieri,
  testimonials,
  maintenanceWorks 
} from '../mock';

const iconMap = {
  'zap': Zap,
  'briefcase': Briefcase,
  'wrench': Wrench,
  'refresh-cw': RefreshCw,
  'building-2': Building2,
  'leaf': Leaf
};

const Home = () => {
  const [activeProject, setActiveProject] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    phone: '',
    message: '',
    website: ''
  });
  const [submitFeedback, setSubmitFeedback] = useState({
    type: 'idle',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVisible, setIsVisible] = useState({});
  const observerRefs = useRef([]);
  const contactEmail = companyInfo.email || 'info@mtsenergia.com';
  const mapsQuery = companyInfo.mapsQuery || companyInfo.address;
  const mapsEmbedUrl = `https://www.google.com/maps?q=${encodeURIComponent(mapsQuery)}&output=embed`;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`;
  const contactEndpoint = process.env.REACT_APP_CONTACT_ENDPOINT || '/contact.php';
  const revealClass = (sectionKey, extra = '') =>
    `${extra} section-reveal ${isVisible[sectionKey] ? 'is-visible' : ''}`.trim();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    observerRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    if (submitFeedback.type !== 'idle') {
      setSubmitFeedback({ type: 'idle', message: '' });
    }
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitFeedback({ type: 'idle', message: '' });

    try {
      const response = await fetch(contactEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          phone: formData.phone || 'Non indicato',
          message: formData.message,
          website: formData.website
        })
      });

      const result = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(result?.message || 'Invio email non riuscito');
      }

      setSubmitFeedback({
        type: 'success',
        message: result?.message || `Messaggio inviato con successo al team MTS Energia su ${companyInfo.administrationEmail}. Ti risponderemo al piu presto.`
      });
      setFormData({ name: '', email: '', subject: '', phone: '', message: '', website: '' });
    } catch (error) {
      console.error('Errore invio email:', error);
      setSubmitFeedback({
        type: 'error',
        message: error.message || `Invio non riuscito. Verifica la configurazione Aruba Mail del sito oppure contattaci direttamente a ${companyInfo.administrationEmail}.`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const AnimatedCounter = ({ value, suffix }) => {
    const [count, setCount] = useState(0);
    const numberValue = parseInt(value.replace(/\D/g, ''));

    useEffect(() => {
      let start = 0;
      const end = numberValue;
      const duration = 2000;
      const increment = end / (duration / 16);

      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);

      return () => clearInterval(timer);
    }, [numberValue]);

    return (
      <span className="text-5xl md:text-6xl font-bold">
        {count}{value.includes('+') ? '+' : ''}{suffix === '%' ? '%' : ''}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-md shadow-sm z-50 transition-all duration-300">
        <nav className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_renewable-energy-hub-19/artifacts/wxu8fra2_logo-MTSenergia-arancio-1.png.webp" 
              alt="MTS Energia Logo" 
              className="h-14 w-auto"
            />
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <a href="#about" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">Chi Siamo</a>
            <a href="#divisions" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">Servizi</a>
            <a href="#projects" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">Portfolio</a>
            <Link to="/codice-etico" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">Codice Etico</Link>
            <a href="#contact" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">Contatti</a>
            <a href="#contact">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white px-6 transition-all duration-300 hover:scale-105">
                Richiedi Preventivo
              </Button>
            </a>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative pt-28 pb-24 overflow-hidden min-h-[calc(100vh-96px)] flex items-center">
        {companyInfo.heroBannerImage ? (
          <div
            className="absolute inset-0 bg-cover bg-center scale-[1.03]"
            style={{ backgroundImage: `url(${companyInfo.heroBannerImage})` }}
          />
        ) : null}
        <div className="absolute left-10 top-32 h-48 w-48 rounded-full bg-orange-500/20 blur-3xl float-slow"></div>
        <div className="absolute bottom-10 right-10 h-56 w-56 rounded-full bg-blue-500/20 blur-3xl float-delayed"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-orange-900/10 via-black/35 to-blue-900/10"></div>
        <div className="absolute inset-0 bg-black/20"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6 flex justify-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-medium text-white/90 backdrop-blur-md shadow-lg">
                <Sparkles className="h-4 w-4 text-orange-300" />
                Thinking Power per energia, manutenzione e costruzioni
              </div>
            </div>
            <div className="mb-8 flex justify-center hero-float">
              <img
                src="https://customer-assets.emergentagent.com/job_renewable-energy-hub-19/artifacts/wxu8fra2_logo-MTSenergia-arancio-1.png.webp"
                alt="MTS Energia"
                className="h-16 md:h-20 w-auto drop-shadow-lg"
              />
            </div>
            <h2 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight drop-shadow-lg text-balance">
              Energia Sostenibile
              <span className="block text-orange-300">Futuro Brillante</span>
            </h2>
            <p className="max-w-2xl mx-auto text-lg md:text-xl text-white/85 leading-relaxed mb-10">
              Dal project development alla manutenzione industriale, MTS Energia accompagna ogni iniziativa con una visione tecnica solida, operativa e orientata ai risultati.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg" 
                className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 hover:shadow-xl group shadow-[0_20px_60px_rgba(249,115,22,0.35)]"
                type="button"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              >
                Inizia il Tuo Progetto
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white/70 text-white hover:bg-white/15 px-8 py-6 text-lg font-semibold transition-all duration-300"
                type="button"
                onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              >
                Scopri di Più
              </Button>
            </div>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
              <div className="glass-panel rounded-2xl px-5 py-4 text-white">
                <p className="text-sm font-semibold text-white">Approccio integrato</p>
                <p className="mt-1 text-sm text-white/75">Strategia, progettazione, O&M e supporto operativo.</p>
              </div>
              <div className="glass-panel rounded-2xl px-5 py-4 text-white">
                <p className="text-sm font-semibold text-white">Esperienza reale</p>
                <p className="mt-1 text-sm text-white/75">Interventi consolidati su impianti e siti industriali.</p>
              </div>
              <div className="glass-panel rounded-2xl px-5 py-4 text-white">
                <p className="text-sm font-semibold text-white">Relazione diretta</p>
                <p className="mt-1 text-sm text-white/75">Contatto chiaro con un referente aziendale qualificato.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 bg-gradient-to-r from-orange-600 to-orange-500 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {statistics.map((stat) => (
              <div key={stat.id} className="text-center group glass-panel rounded-3xl px-4 py-6 hover:-translate-y-1 transition-all duration-300">
                <div className="text-white">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </div>
                <p className="text-orange-100 text-lg mt-2 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section 
        id="about" 
        className="py-24 bg-white"
        ref={(el) => (observerRefs.current[0] = el)}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className={revealClass('about', 'text-center mb-16')}>
              <Badge className="mb-4 bg-blue-100 text-blue-700 px-4 py-2">
                Chi Siamo
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Leader nell'Energia Rinnovabile
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {companyInfo.mission}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mt-16">
              <Card className={`${revealClass('about', 'stagger-1')} card-interactive border-0 shadow-lg bg-gradient-to-br from-white to-orange-50`}>
                <CardHeader>
                  <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                    <Target className="w-7 h-7 text-orange-600" />
                  </div>
                  <CardTitle className="text-2xl">Missione</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 leading-relaxed">
                    Sviluppo sostenibile attraverso la diversificazione delle energie rinnovabili e l'efficienza energetica.
                  </p>
                </CardContent>
              </Card>

              <Card className={`${revealClass('about', 'stagger-2')} card-interactive border-0 shadow-lg bg-gradient-to-br from-white to-blue-50`}>
                <CardHeader>
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <Award className="w-7 h-7 text-blue-600" />
                  </div>
                  <CardTitle className="text-2xl">Esperienza</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 leading-relaxed">
                    Dal 2007 realizziamo impianti ad energia rinnovabile con competenze specifiche in ogni fase progettuale.
                  </p>
                </CardContent>
              </Card>

              <Card className={`${revealClass('about', 'stagger-3')} card-interactive border-0 shadow-lg bg-gradient-to-br from-white to-green-50`}>
                <CardHeader>
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-7 h-7 text-green-600" />
                  </div>
                  <CardTitle className="text-2xl">Collaborazione</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 leading-relaxed">
                    Lavoriamo con investitori, trader e partner qualificati per strutture di project finance solide.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Divisions Section - Improved with sub-services */}
      <section 
        id="divisions" 
        className="py-24 bg-gradient-to-b from-gray-50 to-white"
        ref={(el) => (observerRefs.current[1] = el)}
      >
        <div className="container mx-auto px-4">
          <div className={revealClass('divisions', 'text-center mb-16')}>
            <Badge className="mb-4 bg-orange-100 text-orange-700 px-4 py-2">
              Le Nostre Divisioni
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Soluzioni Complete per l'Energia
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Dalla progettazione alla manutenzione, un servizio integrato per ogni esigenza
            </p>
          </div>

          {/* Divisione Energia */}
          <div className={`max-w-7xl mx-auto mb-20 ${revealClass('divisions')}`}>
            <div className="relative overflow-hidden rounded-3xl p-8 md:p-12 shadow-xl border border-orange-100 hover:shadow-2xl transition-all duration-500">
              {companyInfo.divisionsBackgroundImage ? (
                <div className="absolute inset-0">
                  <div
                    className="absolute inset-0 bg-cover bg-center scale-110"
                    style={{ backgroundImage: `url(${companyInfo.divisionsBackgroundImage})` }}
                  />
                  <div className="absolute inset-0 backdrop-blur-md bg-white/80" />
                </div>
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-white" />
              )}
              <div className="relative z-10">
              <div className="flex flex-col items-center text-center gap-5 mb-10">
                <div className="flex items-center gap-4 justify-center">
                  <div className="w-16 h-16 bg-orange-600 rounded-2xl flex items-center justify-center">
                    <Zap className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900">Divisione Energia</h3>
                    <p className="text-gray-600">Impianti per la produzione di energia rinnovabile</p>
                  </div>
                </div>
                <Link to="/divisione-energia" className="w-full flex justify-center">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-6 text-lg font-semibold w-full sm:w-[260px]">
                    Vai <ArrowRight className="ml-2" />
                  </Button>
                </Link>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <Link to="/divisione-energia/fotovoltaici" className="block">
                  <Card className="border-2 border-orange-200 hover:border-orange-400 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 bg-white cursor-pointer h-full card-interactive">
                    <CardHeader>
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-3">
                        <Zap className="w-6 h-6 text-orange-600" />
                      </div>
                      <CardTitle className="text-xl">Fotovoltaici</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Impianti a terra e in elevazione con kilowattaggi di qualsiasi dimensione
                      </p>
                    </CardContent>
                  </Card>
                </Link>

                <Link to="/divisione-energia/eolici" className="block">
                  <Card className="border-2 border-blue-200 hover:border-blue-400 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 bg-white cursor-pointer h-full card-interactive">
                    <CardHeader>
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-3">
                        <RefreshCw className="w-6 h-6 text-blue-600" />
                      </div>
                      <CardTitle className="text-xl">Eolici</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Generatori eolici di grandi e piccole dimensioni (mini-eolico)
                      </p>
                    </CardContent>
                  </Card>
                </Link>

                <Link to="/divisione-energia/biomasse" className="block">
                  <Card className="border-2 border-green-200 hover:border-green-400 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 bg-white cursor-pointer h-full card-interactive">
                    <CardHeader>
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-3">
                        <Leaf className="w-6 h-6 text-green-600" />
                      </div>
                      <CardTitle className="text-xl">Biomasse</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Prodotti agricoli, materiali organici e residui agro-forestali
                      </p>
                    </CardContent>
                  </Card>
                </Link>

                <Link to="/divisione-energia/idroelettrici" className="block">
                  <Card className="border-2 border-cyan-200 hover:border-cyan-400 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 bg-white cursor-pointer h-full card-interactive">
                    <CardHeader>
                      <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center mb-3">
                        <TrendingUp className="w-6 h-6 text-cyan-600" />
                      </div>
                      <CardTitle className="text-xl">Idroelettrici</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Piccole e grandi dimensioni con tecnologie eco-compatibili
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-2xl border border-gray-200">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-6 h-6 text-orange-600" />
                    Operation & Maintenance
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2"></span>
                      <span>Monitoraggio e controllo per ottimizzazione e massimo rendimento</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2"></span>
                      <span>Gestione amministrativa secondo normativa vigente</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2"></span>
                      <span>Gestione strategica del portafoglio energetico</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-gray-200">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Briefcase className="w-6 h-6 text-blue-600" />
                    Consulting
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2"></span>
                      <span>Identificazione opportunità d'investimento</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2"></span>
                      <span>Pre-due diligence tecnica, legale e commerciale</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2"></span>
                      <span>Project finance e assistenza nelle compravendite</span>
                    </li>
                  </ul>
                </div>
              </div>
              </div>
            </div>
          </div>

          {/* Divisione Manutenzione */}
          <div className={`max-w-7xl mx-auto mb-20 ${revealClass('divisions', 'stagger-1')}`}>
            <div className="relative overflow-hidden rounded-3xl p-8 md:p-12 shadow-xl border border-blue-100 hover:shadow-2xl transition-all duration-500">
              {companyInfo.divisionsBackgroundImage ? (
                <div className="absolute inset-0">
                  <div
                    className="absolute inset-0 bg-cover bg-center scale-110"
                    style={{ backgroundImage: `url(${companyInfo.divisionsBackgroundImage})` }}
                  />
                  <div className="absolute inset-0 backdrop-blur-md bg-white/80" />
                </div>
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-white" />
              )}
              <div className="relative z-10">
              <div className="flex flex-col items-center text-center gap-5 mb-10">
                <div className="flex items-center gap-4 justify-center">
                  <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center">
                    <Wrench className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900">Divisione Manutenzione</h3>
                    <p className="text-gray-600">Manutenzione e gestione di impianti industriali</p>
                  </div>
                </div>
                <Link to="/divisione-manutenzione" className="w-full flex justify-center">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-6 text-lg font-semibold w-full sm:w-[260px]">
                    Vai <ArrowRight className="ml-2" />
                  </Button>
                </Link>
              </div>

              <div className="grid md:grid-cols-4 gap-6 mb-8">
                <Link to="/divisione-manutenzione/periodica" className="block">
                  <div className="bg-white p-6 rounded-2xl border-2 border-blue-200 hover:border-blue-400 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer h-full card-interactive">
                    <h4 className="font-bold text-blue-600 mb-2">Periodica</h4>
                    <p className="text-sm text-gray-600">Controlli ricorrenti programmati</p>
                  </div>
                </Link>
                <Link to="/divisione-manutenzione/straordinaria" className="block">
                  <div className="bg-white p-6 rounded-2xl border-2 border-purple-200 hover:border-purple-400 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer h-full card-interactive">
                    <h4 className="font-bold text-purple-600 mb-2">Straordinaria</h4>
                    <p className="text-sm text-gray-600">Interventi di riparazione</p>
                  </div>
                </Link>
                <Link to="/divisione-manutenzione/pianificata" className="block">
                  <div className="bg-white p-6 rounded-2xl border-2 border-green-200 hover:border-green-400 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer h-full card-interactive">
                    <h4 className="font-bold text-green-600 mb-2">Pianificata</h4>
                    <p className="text-sm text-gray-600">Pianificazione controlli</p>
                  </div>
                </Link>
                <Link to="/divisione-manutenzione/preventiva" className="block">
                  <div className="bg-white p-6 rounded-2xl border-2 border-orange-200 hover:border-orange-400 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer h-full card-interactive">
                    <h4 className="font-bold text-orange-600 mb-2">Preventiva</h4>
                    <p className="text-sm text-gray-600">Prevenzione usura e rotture</p>
                  </div>
                </Link>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-2xl border border-gray-200">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Building2 className="w-6 h-6 text-blue-600" />
                    Montaggio & Costruzioni
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2"></span>
                      <span>Montaggio impianti industriali certificati</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2"></span>
                      <span>Progettazione e montaggio capannoni</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2"></span>
                      <span>Impianti industriali chiavi in mano</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-gray-200">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <RefreshCw className="w-6 h-6 text-purple-600" />
                    Revamping
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2"></span>
                      <span>Rinnovo impianti energetici obsoleti</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2"></span>
                      <span>Upgrade caldaie e termovalorizzatori</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2"></span>
                      <span>Massima produttività ed efficienza</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-r from-blue-600 to-blue-500 p-6 rounded-2xl text-white">
                <div className="flex items-start gap-4">
                  <Award className="w-8 h-8 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-lg mb-2">Obiettivi della Manutenzione</h4>
                    <div className="grid md:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Sicurezza dei lavoratori</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Aumento vita linee produttive</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Riduzione strategica consumi</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Ottimizzazione risorse</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            </div>
          </div>

          {/* Altri Servizi */}
          <div className={`max-w-7xl mx-auto ${revealClass('divisions', 'stagger-2')}`}>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-green-50 to-white card-interactive">
                <CardHeader>
                  <div className="w-14 h-14 bg-green-600 rounded-2xl flex items-center justify-center mb-4">
                    <Leaf className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-2xl">Risparmio Energetico</CardTitle>
                  <CardDescription className="text-base">Up-grade degli Impianti</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    Interventi su impianti esistenti per ridurre il fabbisogno energetico del cliente.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="text-sm">Studio preliminare consumi e audit gratuito</span>
                    </li>
                    <li className="flex items-center gap-2 text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="text-sm">Energy Performance Contract (ES.Co.)</span>
                    </li>
                    <li className="flex items-center gap-2 text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="text-sm">Nessun rischio tecnico e finanziario</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-red-50 to-white card-interactive">
                <CardHeader>
                  <div className="w-14 h-14 bg-red-600 rounded-2xl flex items-center justify-center mb-4">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-2xl">Divisione Edilizia</CardTitle>
                  <CardDescription className="text-base">Costruzioni</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    Esperienza nella costruzione industriale e civile, con approccio integrato.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-red-600" />
                      <span className="text-sm">Nuove costruzioni e ristrutturazioni</span>
                    </li>
                    <li className="flex items-center gap-2 text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-red-600" />
                      <span className="text-sm">Edifici residenziali, produttivi e industriali</span>
                    </li>
                    <li className="flex items-center gap-2 text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-red-600" />
                      <span className="text-sm">Opere civili e industriali 'chiavi in mano'</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Portfolio Section */}
      <section 
        id="projects" 
        className="py-24 bg-white"
        ref={(el) => (observerRefs.current[2] = el)}
      >
        <div className="container mx-auto px-4">
          <div className={revealClass('projects', 'text-center mb-16')}>
            <Badge className="mb-4 bg-green-100 text-green-700 px-4 py-2">
              Portfolio
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Progetti Realizzati
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Oltre 30 MW di potenza installata in Italia e all'estero
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {projects.map((project, index) => (
              <Card 
                key={project.id} 
                className={`group border-0 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden hover:-translate-y-2 card-interactive ${revealClass('projects')}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative overflow-hidden h-56">
                  <img 
                    src={project.image} 
                    alt={project.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <Badge className="absolute top-4 right-4 bg-orange-600 text-white">
                    {project.power}
                  </Badge>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl group-hover:text-orange-600 transition-colors">
                    {project.name}
                  </CardTitle>
                  <CardDescription className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {project.location}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Tipologia:</span>
                      <span className="font-semibold text-gray-900">{project.type}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Moduli:</span>
                      <span className="font-semibold text-gray-900">{project.modules}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className={revealClass('projects', 'text-center mb-16')}>
            <Badge className="mb-4 bg-blue-100 text-blue-700 px-4 py-2">
              Cantieri
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Operatività Cantieri
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Foto e esempi reali dei lavori in corso e completati
            </p>
          </div>

          <div className="max-w-7xl mx-auto space-y-16">
            {operativitaCantieri.map((cantiere) => {
              const isFour = (cantiere.images?.length || 0) === 4;
              const gridCols = isFour ? "lg:grid-cols-2" : "lg:grid-cols-3";

              return (
                <div key={cantiere.id} className={`space-y-6 ${revealClass('projects')}`}>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-3 text-sm font-semibold tracking-widest text-gray-800">
                      <span className="h-5 w-0.5 bg-green-600" />
                      <span className="h-5 w-0.5 bg-red-600" />
                      <span>REFERENZE | LAVORI ESEGUITI</span>
                      <span className="h-5 w-0.5 bg-red-600" />
                      <span className="h-5 w-0.5 bg-green-600" />
                    </div>
                    <h3 className="mt-2 text-xl md:text-2xl font-semibold text-gray-900">
                      {cantiere.title}
                    </h3>
                  </div>

                  <div className={`grid grid-cols-1 md:grid-cols-2 ${gridCols} gap-6`}>
                    {cantiere.images?.map((src, idx) => (
                      <div key={`${cantiere.id}-${idx}`} className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50 card-interactive">
                        <img
                          src={src}
                          alt={`${cantiere.title} - ${idx + 1}`}
                          className="w-full h-full object-cover aspect-[16/10] transition-transform duration-700 hover:scale-105"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-24 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className={revealClass('projects', 'text-center mb-16')}>
            <Badge className="mb-4 bg-orange-100 text-orange-700 px-4 py-2">
              Risultati Concreti
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Rispamio Energetico: Lavori Eseguiti
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Risparmio energetico certificato fino al 57%
            </p>
          </div>

          <div
            className={`grid grid-cols-1 gap-8 max-w-6xl mx-auto ${
              caseStudies.length === 1 ? "place-items-center md:grid-cols-1" : "md:grid-cols-2"
            }`}
          >
            {caseStudies.map((study) => (
              <Card key={study.id} className={`border-0 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden w-full max-w-xl card-interactive ${revealClass('projects')}`}>
                {study.image && (
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={study.image} 
                      alt={study.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20">
                      <Link to={`/portfolio/${study.slug}`}>
                        <Button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-sm font-semibold">
                          Vai <ArrowRight className="ml-2" />
                        </Button>
                      </Link>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 text-white z-10 text-center">
                      <h3 className="text-2xl font-bold mb-1">{study.title}</h3>
                      <p className="text-orange-100 flex items-center gap-2 justify-center">
                        <MapPin className="w-4 h-4" />
                        {study.location}
                      </p>
                    </div>
                  </div>
                )}
                <CardContent className="p-8">
                  <div className="mb-6">
                    <div className="flex items-center justify-center mb-4">
                      <div className="relative">
                        <div className="w-32 h-32 rounded-full border-8 border-green-100 flex items-center justify-center">
                          <div className="text-center">
                            <span className="text-5xl font-bold text-green-600">{study.savings}%</span>
                            <p className="text-xs text-gray-600 mt-1">Risparmio</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Potenza</p>
                      <p className="text-lg font-bold text-gray-900">{study.power}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Installazione</p>
                      <p className="text-lg font-bold text-gray-900">{study.installation}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      Interventi Realizzati
                    </h4>
                    {study.interventions.map((intervention, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-gray-700 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                        {intervention}
                      </div>
                    ))}
                  </div>

                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section 
        id="contact" 
        className="py-24 bg-gradient-to-b from-slate-50 via-white to-white"
        ref={(el) => (observerRefs.current[3] = el)}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className={revealClass('contact', 'text-center max-w-3xl mx-auto mb-14')}>
              <Badge className="mb-4 bg-orange-100 text-orange-700 px-4 py-2">
                Contatti
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5">
                Parliamo del Tuo Progetto
              </h2>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                Un confronto diretto, informazioni chiare e riferimenti aziendali completi per avviare il tuo prossimo intervento energetico con MTS Energia.
              </p>
            </div>

            <div className={`grid xl:grid-cols-[1.15fr,0.85fr] gap-8 items-start ${revealClass('contact')}`}>
              <div className="space-y-6">
                <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 text-white shadow-2xl">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(249,115,22,0.25),transparent_32%)]" />
                  <div className="relative z-10">
                    <div className="flex flex-wrap items-center gap-3 mb-5">
                      <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-orange-200">
                        MTS Energia
                      </span>
                      <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-sm text-slate-100">
                        <Clock3 className="h-4 w-4 text-orange-300" />
                        Riscontro rapido
                      </span>
                    </div>

                    <h3 className="text-2xl md:text-3xl font-bold leading-tight mb-4">
                      Professionisti delle rinnovabili con un contatto diretto e trasparente
                    </h3>
                    <p className="text-slate-200 leading-relaxed max-w-2xl">
                      Dalla prima richiesta alla consulenza operativa, il team MTS Energia affianca aziende e investitori con competenza tecnica, esperienza sul campo e riferimenti societari chiari.
                    </p>

                    <div className="grid sm:grid-cols-3 gap-3 mt-8">
                      <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                        <p className="text-sm font-semibold text-white">Consulenza iniziale</p>
                        <p className="text-sm text-slate-200 mt-1">Primo confronto dedicato al progetto.</p>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                        <p className="text-sm font-semibold text-white">Assistenza tecnica</p>
                        <p className="text-sm text-slate-200 mt-1">Supporto per energia, manutenzione e revamping.</p>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                        <p className="text-sm font-semibold text-white">Contatto aziendale</p>
                        <p className="text-sm text-slate-200 mt-1">Invio diretto a {contactEmail}.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="border-0 shadow-xl bg-white card-interactive">
                    <CardContent className="p-7 space-y-5">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0">
                          <ShieldCheck className="w-6 h-6" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-orange-600">Referente</p>
                          <h3 className="text-2xl font-bold text-gray-900 mt-1">{companyInfo.contactPerson}</h3>
                          <p className="text-gray-600 mt-2">{companyInfo.contactRole}</p>
                          <p className="text-sm text-gray-500 mt-1">{companyInfo.certification}</p>
                        </div>
                      </div>

                      <div className="space-y-4 text-sm text-gray-700">
                        <div className="flex items-start gap-3">
                          <Phone className="w-4 h-4 text-orange-600 mt-0.5" />
                          <div>
                            <p className="font-semibold text-gray-900">Contatti telefonici</p>
                            <a href={`tel:${companyInfo.mobile.replace(/\s+/g, '')}`} className="block hover:text-orange-600 transition-colors">
                              Mobile: {companyInfo.mobile}
                            </a>
                            <a href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`} className="block hover:text-orange-600 transition-colors">
                              Tel: {companyInfo.phone}
                            </a>
                            <p>Fax: {companyInfo.fax}</p>
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <Mail className="w-4 h-4 text-blue-600 mt-0.5" />
                          <div>
                            <p className="font-semibold text-gray-900">Email aziendali</p>
                            <a href={`mailto:${contactEmail}`} className="block hover:text-orange-600 transition-colors">
                              {contactEmail}
                            </a>
                            <a href={`mailto:${companyInfo.administrationEmail}`} className="block hover:text-orange-600 transition-colors">
                              {companyInfo.administrationEmail}
                            </a>
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-green-600 mt-0.5" />
                          <div>
                            <p className="font-semibold text-gray-900">P. IVA</p>
                            <p>{companyInfo.vat}</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-xl bg-white card-interactive">
                    <CardContent className="p-7 space-y-6">
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-600">Sedi</p>
                        <h3 className="text-2xl font-bold text-gray-900 mt-1">Presenza sul territorio</h3>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                        <p className="text-sm font-semibold text-gray-900 mb-2">Sede legale</p>
                        <p className="text-sm leading-relaxed text-gray-600">{companyInfo.registeredOffice}</p>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                        <p className="text-sm font-semibold text-gray-900 mb-2">Uffici operativi</p>
                        <p className="text-sm leading-relaxed text-gray-600">{companyInfo.operationalOffice}</p>
                      </div>

                      <a
                        href={mapsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors"
                      >
                        Apri su Google Maps
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </CardContent>
                  </Card>
                </div>

                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl card-interactive">
                  <div className="aspect-[16/10] bg-slate-100">
                    <iframe
                      title="Mappa MTS Energia"
                      src={mapsEmbedUrl}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      className="h-full w-full border-0"
                    />
                  </div>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-6">
                    <div>
                      <p className="text-lg font-semibold text-gray-900">Cassino (FR), Italia</p>
                      <p className="text-sm text-gray-600 mt-1">
                        Una presenza locale per seguire con precisione interventi, sopralluoghi e richieste commerciali.
                      </p>
                    </div>
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
                    >
                      Apri la mappa
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>

              <Card className={`border-0 shadow-2xl overflow-hidden bg-white card-interactive ${revealClass('contact', 'stagger-2')}`}>
                <div className="bg-gradient-to-r from-orange-600 to-orange-500 p-8 text-white">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/15 mb-5">
                    <Send className="w-7 h-7" />
                  </div>
                  <CardTitle className="text-3xl text-white">Richiedi Informazioni</CardTitle>
                </div>

                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="hidden" aria-hidden="true">
                      <label htmlFor="website">Website</label>
                      <input
                        id="website"
                        type="text"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Nome e Cognome *</label>
                      <Input 
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Mario Rossi"
                        required
                        className="w-full h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-orange-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                      <Input 
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="mario.rossi@email.com"
                        required
                        className="w-full h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-orange-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Oggetto *</label>
                      <Input 
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        placeholder="Richiesta informazioni per impianto o servizio"
                        required
                        className="w-full h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-orange-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Telefono</label>
                      <Input 
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+39 123 456 7890"
                        className="w-full h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-orange-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Messaggio *</label>
                      <Textarea 
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Descrivi il tuo progetto, il contesto operativo o il tipo di supporto richiesto..."
                        required
                        rows={5}
                        className="w-full rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-orange-500"
                      />
                    </div>

                    {submitFeedback.message ? (
                      <div
                        className={`rounded-2xl px-4 py-3 text-sm ${
                          submitFeedback.type === 'error'
                            ? 'border border-red-200 bg-red-50 text-red-700'
                            : 'border border-green-200 bg-green-50 text-green-700'
                        }`}
                      >
                        {submitFeedback.message}
                      </div>
                    ) : null}

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg font-semibold transition-all duration-300 hover:scale-[1.01]"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Invio in corso...
                        </>
                      ) : (
                        <>
                          Invia Richiesta
                          <ArrowRight className="ml-2" />
                        </>
                      )}
                    </Button>
                  </form>

                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_renewable-energy-hub-19/artifacts/wxu8fra2_logo-MTSenergia-arancio-1.png.webp" 
                  alt="MTS Energia Logo" 
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                Professionisti delle rinnovabili dal {companyInfo.foundingYear}
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-lg">Servizi</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#divisions" className="hover:text-orange-400 transition-colors">Operation & Maintenance</a></li>
                <li><a href="#divisions" className="hover:text-orange-400 transition-colors">Consulting</a></li>
                <li><a href="#divisions" className="hover:text-orange-400 transition-colors">Revamping</a></li>
                <li><a href="#divisions" className="hover:text-orange-400 transition-colors">Costruzioni</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-lg">Azienda</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#about" className="hover:text-orange-400 transition-colors">Chi Siamo</a></li>
                <li><a href="#projects" className="hover:text-orange-400 transition-colors">Portfolio</a></li>
                <li><a href="#contact" className="hover:text-orange-400 transition-colors">Contatti</a></li>
                <li><a href="#" className="hover:text-orange-400 transition-colors">Carriere</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-lg">Contatti</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {companyInfo.phone}
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {companyInfo.email}
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Cassino (FR)
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} {companyInfo.legalName} - P.IVA: {companyInfo.vat}
              </p>
              <div className="flex gap-6 text-sm text-gray-400">
                {/* <a href="#" className="hover:text-orange-400 transition-colors">Privacy Policy</a>
                <a href="#" className="hover:text-orange-400 transition-colors">Cookie Policy</a>
                <a href="#" className="hover:text-orange-400 transition-colors">Termini e Condizioni</a> */}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
